/* eslint-disable react-hooks/exhaustive-deps */
/**
 * SingleContactPage - Clean 3-tab design focused on contact details and documents
 */
import React, { useEffect, useState, useCallback } from 'react';
import { LayoutWrapper } from 'app/components/LayoutWrapper/Loadable';
import { useLayoutSlice } from 'app/components/LayoutWrapper/slice';
import { selectLayout } from 'app/components/LayoutWrapper/slice/selectors';
import { Helmet } from 'react-helmet-async';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { routes } from 'utils/routes';
import { useSingleContactSlice } from './slice';
import { selectSingleContact } from './slice/selectors';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import './styles.scss';
import { Link } from 'react-router-dom';
import { CompanyPoc } from './CompanyPoc/Loadable';
import { ObjectID } from 'bson';
import {
  editCompanyContactsRolesList,
  isAuthorized,
} from 'utils/roleaccesslist';
import { QuickViewModal } from '../CompanyDashboard/QuickViewModal';

// Last Interaction Logic with Time Ago
const getTimeAgo = (date: string) => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  if (diffInSeconds < 31536000)
    return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  return `${Math.floor(diffInSeconds / 31536000)}y ago`;
};

const getLastInteractionData = (agreements: any[]) => {
  if (!agreements || agreements.length === 0)
    return { date: 'Never', description: '' };

  const allHistoryItems = agreements
    .flatMap(agreement =>
      (agreement.history || []).map(item => ({
        ...item,
        documentTitle: agreement.agreement?.agreementTitle,
      })),
    )
    .filter(item => item.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (allHistoryItems.length === 0) return { date: 'Never', description: '' };

  const lastItem = allHistoryItems[0];
  const getActionDescription = (action, data) => {
    switch (action) {
      case 'signed':
        return 'signed the document';
      case 'approved':
        return 'approved the document';
      case 'declined':
        return 'declined the document';
      case 'viewed':
        return 'viewed the document';
      case 'submitted':
        return `sent document to ${data?.partyName || 'recipient'}`;
      case 'created':
        return 'created the document';
      case 'updated':
        return 'updated the document';
      default:
        return action || 'performed an action';
    }
  };

  return {
    date: getTimeAgo(lastItem.date),
    description: `${lastItem.party || 'User'} ${getActionDescription(
      lastItem.action,
      lastItem.data,
    )} "${lastItem.documentTitle}"`,
  };
};

// Document Status Helper
const getDocumentStatus = (agreementStatus: string) => {
  switch (agreementStatus) {
    case 'drafted':
      return { text: 'Draft', color: '#6b7280', bg: '#f3f4f6' };
    case 'mapped':
    case 'approved':
      return { text: 'Pending', color: '#d97706', bg: '#fef3c7' };
    case 'recipients_partially_signed':
    case 'recipients_signed':
    case 'signers_partially_signed':
    case 'signers_signed':
      return { text: 'In Progress', color: '#2563eb', bg: '#dbeafe' };
    case 'binding':
      return { text: 'Completed', color: '#059669', bg: '#d1fae5' };
    case 'declined':
      return { text: 'Declined', color: '#dc2626', bg: '#fee2e2' };
    default:
      return {
        text: agreementStatus || 'Unknown',
        color: '#6b7280',
        bg: '#f3f4f6',
      };
  }
};

interface Props {
  isModularView?: boolean;
  onCancel?: Function;
  contactId?: string;
  contactType?: string;
  isOnlyContactsView?: boolean;
  onUpdateSuccess?: Function;
}

type POC = {
  defaultSigner: boolean;
  email: string;
  name: string;
  title: string;
  _id: any;
};

const defaultPOC: POC = {
  defaultSigner: false,
  email: '',
  name: '',
  title: '',
  _id: new ObjectID(),
};

export function SingleContactPage(props: Props) {
  const [companyPOCList, setCompanyPOCList] = useState<POC[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [localFields, setLocalFields] = useState({});
  const [quickViewDocument, setQuickViewDocument] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const { actions } = useSingleContactSlice();
  useLayoutSlice();
  const singleContactState = useSelector(selectSingleContact);
  const layoutState = useSelector(selectLayout);
  const dispatch = useDispatch();
  const { companyId, contactId, contactType } = useParams<any>();

  // Check if contact is a team member
  const isTeamMember = useCallback(() => {
    if (
      !singleContactState.contact?._id ||
      singleContactState.contact.contactType !== 'individual'
    ) {
      return false;
    }
    return layoutState.selectedCompany?.members?.some(
      m => m.contactId === singleContactState.contact._id,
    );
  }, [singleContactState.contact, layoutState.selectedCompany?.members]);

  useEffect(() => {
    const cId = contactId || props.contactId;
    const cType = contactType || props.contactType;

    dispatch(actions.resetForm());
    dispatch(actions.resetContact());
    dispatch(
      actions.getSingleContact({
        companyId,
        contactId: cId,
        contactType: cType,
      }),
    );
    return () => {
      dispatch(actions.resetContact());
    };
  }, [companyId, contactId, contactType, props.contactId, props.contactType]);

  useEffect(() => {
    if (singleContactState.contact?.contactType === 'company') {
      setCompanyPOCList(singleContactState.contact?.companySigners || []);
      setIsMounted(true);
    }
  }, [singleContactState.contact, singleContactState.contact?.companySigners]);

  useEffect(() => {
    setLocalFields({});
  }, [singleContactState.contact]);

  useEffect(() => {
    if (singleContactState.contact?.contactType === 'individual') {
      setIsMounted(true);
    }
  }, [singleContactState.contact]);

  useEffect(() => {
    if (singleContactState.contactError)
      toast.error(singleContactState.contactError);
  }, [singleContactState.contactError]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setLocalFields(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleFieldBlur = useCallback(
    (fieldName, value) => {
      dispatch(
        actions.changeFieldValue({
          fieldName,
          value,
        }),
      );
    },
    [dispatch, actions],
  );

  const getFieldValue = useCallback(
    fieldName => {
      return localFields[fieldName] !== undefined
        ? localFields[fieldName]
        : singleContactState.fields[fieldName] || '';
    },
    [localFields, singleContactState.fields],
  );

  const onUpdateContact = () => {
    setIsLoading(true);

    if (!singleContactState.fields.name?.trim()) {
      toast.error('Name is required');
      setIsLoading(false);
      return;
    }

    if (!singleContactState.fields.email?.trim()) {
      toast.error('Email is required');
      setIsLoading(false);
      return;
    }

    if (contactType === 'company' || props.contactType === 'company') {
      const invalidEmail = singleContactState.contact.companySigners?.find(
        poc => poc.email && !/\S+@\S+\.\S+/.test(poc.email),
      );

      if (invalidEmail) {
        toast.error('Please check if all signer email ids are valid');
        setIsLoading(false);
        return;
      }
    }

    const updateRequest = {
      companyId: layoutState.selectedCompany?._id,
      _id: singleContactState.contact._id,
      contactType: singleContactState.contact.contactType,
      name: singleContactState.fields.name,
      email: singleContactState.fields.email,
      phone: singleContactState.fields.phone,
      legalAddress: {
        street: singleContactState.fields.street || '',
        street2: singleContactState.fields.street2 || '',
        city: singleContactState.fields.city || '',
        state: singleContactState.fields.state || '',
        zipCode: singleContactState.fields.zipCode || '',
        country: singleContactState.fields.country || '',
        district: singleContactState.fields.district || '',
      },
      categories: singleContactState.contact.categories || [],
      notes:
        singleContactState.fields.notes ||
        singleContactState.contact.notes ||
        '',
      incomplete: false,

      ...(singleContactState.contact.contactType === 'individual' && {
        jobTitle:
          singleContactState.fields.jobTitle ||
          singleContactState.contact.jobTitle ||
          '',
        companyName:
          singleContactState.fields.companyName ||
          singleContactState.contact.companyName ||
          '',
        userProperties: singleContactState.contact.userProperties || [],
        additionalInfo: singleContactState.contact.additionalInfo || {},
      }),

      ...(singleContactState.contact.contactType === 'company' && {
        companyName: singleContactState.fields.name,
        noticeEmail: singleContactState.fields.email,
        companyCorporateFormation:
          singleContactState.fields.companyCorporateFormation ||
          singleContactState.contact.companyCorporateFormation ||
          '',
        companyCorporateFormationState:
          singleContactState.fields.companyCorporateFormationState ||
          singleContactState.contact.companyCorporateFormationState ||
          '',
        companySigners: singleContactState.contact.companySigners || [],
      }),
    };

    dispatch(
      actions.updateSingleContact({
        request: updateRequest,
        successCb: data => {
          setIsLoading(false);
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
          if (props.onUpdateSuccess) props.onUpdateSuccess(data);
        },
        errorCb: () => {
          setIsLoading(false);
        },
      }),
    );
  };

  const onAddNewPOC = () => {
    if (companyPOCList.filter(poc => !poc.email || !poc.name).length <= 0) {
      const newList = [
        ...companyPOCList,
        { ...defaultPOC, _id: new ObjectID() },
      ];
      setCompanyPOCList(newList);
      dispatch(actions.updateContactPOCList(newList));
    } else {
      toast.error(
        'Please remove signer with empty name or email before adding new',
      );
    }
  };

  const onPOCValueChange = (index, field, value) => {
    const tempPOCList = companyPOCList.map((poc, i) => {
      if (i === index) return { ...poc, [field]: value };
      else
        return field === 'defaultSigner'
          ? { ...poc, defaultSigner: false }
          : poc;
    });

    setCompanyPOCList(tempPOCList);
    dispatch(actions.updateContactPOCList(tempPOCList));
  };

  const onRemovePOC = id => {
    const newList = companyPOCList.filter(c => c._id !== id);
    setCompanyPOCList(newList);
    dispatch(actions.updateContactPOCList(newList));
  };

  // Get last interaction data
  const lastInteractionData = getLastInteractionData(
    singleContactState.contactAgreements || [],
  );

  return (
    <>
      <Helmet>
        <title>Contact Details</title>
        <meta name="description" content="Contact management page" />
      </Helmet>

      <LayoutWrapper
        title="Contact Details"
        subTitle={
          singleContactState.contact?.name ||
          singleContactState.contact?.companyName ||
          'Loading...'
        }
        extraButtons={[
          {
            type: 'primary',
            text: isLoading ? 'Saving...' : 'Save Changes',
            disabled:
              singleContactState.isUpdatingContact ||
              isLoading ||
              !isAuthorized(
                layoutState.userRoles,
                editCompanyContactsRolesList,
              ),
            onClick: () => {
              isAuthorized(layoutState.userRoles, editCompanyContactsRolesList)
                ? onUpdateContact()
                : toast.error('You are not authorized to edit this contact');
            },
          },
          ...(props.isModularView
            ? [
                {
                  type: 'outline',
                  text: 'Cancel',
                  onClick: () => {
                    if (props.onCancel) props.onCancel();
                  },
                  disabled: singleContactState.isUpdatingContact || isLoading,
                },
              ]
            : []),
        ]}
        isModularView={props.isModularView}
      >
        {isMounted ? (
          <div className="contact-page-container">
            {/* Contact Header */}
            <div className="contact-header-card">
              <div className="contact-header-info">
                <div className="contact-details">
                  <h1 className="contact-title">
                    {singleContactState.contact?.name ||
                      singleContactState.contact?.companyName}
                  </h1>
                  <div className="contact-badges">
                    <span
                      className={`contact-type-badge ${
                        singleContactState.contact?.contactType === 'company'
                          ? 'company'
                          : 'individual'
                      }`}
                    >
                      {singleContactState.contact?.contactType === 'company'
                        ? 'Company'
                        : 'Individual'}
                    </span>
                    {isTeamMember() && (
                      <span className="team-member-badge">Team Member</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Last Interaction */}
              <div className="last-interaction-card">
                <div className="last-interaction-content">
                  <div className="last-interaction-header">
                    <span className="last-interaction-label">
                      Last Interaction
                    </span>
                    <div className="last-interaction-date">
                      {lastInteractionData.date}
                    </div>
                  </div>
                  {lastInteractionData.description ? (
                    <div className="last-interaction-description">
                      {lastInteractionData.description}
                    </div>
                  ) : (
                    'No activity recorded'
                  )}
                </div>
              </div>

              {showSuccessMessage && (
                <div className="success-message">
                  <i className="gg-check" />
                  Contact updated successfully!
                </div>
              )}
            </div>

            {/* Tabs */}
            <Tabs className="contact-tabs">
              <TabList className="contact-tab-list">
                <Tab className="contact-tab">Contact Details</Tab>
                <Tab className="contact-tab">
                  Documents ({singleContactState.contactAgreements?.length || 0}
                  )
                </Tab>
                <Tab className="contact-tab">History</Tab>
              </TabList>

              {/* Tab 1: Contact Details */}
              <TabPanel>
                <div className="tab-panel-content">
                  <div className="form-main-content">
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        onUpdateContact();
                      }}
                    >
                      {/* Basic Information */}
                      <div className="form-section">
                        <h3 className="section-title">Basic Information</h3>
                        <div className="form-grid">
                          <div className="form-field">
                            <label className="field-label">
                              {singleContactState.contact?.contactType ===
                              'company'
                                ? 'Company Name'
                                : 'Full Name'}{' '}
                              *
                            </label>
                            <input
                              className={`field-input ${
                                isTeamMember() ? 'disabled' : ''
                              }`}
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                ) || isTeamMember()
                              }
                              required
                              value={getFieldValue('name')}
                              type="text"
                              onChange={e =>
                                handleFieldChange('name', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('name', e.target.value)
                              }
                              placeholder={
                                singleContactState.contact?.contactType ===
                                'company'
                                  ? 'Enter company name'
                                  : 'Enter full name'
                              }
                            />
                          </div>

                          <div className="form-field">
                            <label className="field-label">
                              Email Address *
                            </label>
                            <input
                              className={`field-input ${
                                isTeamMember() ? 'disabled' : ''
                              }`}
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                ) || isTeamMember()
                              }
                              required
                              value={getFieldValue('email')}
                              type="email"
                              onChange={e =>
                                handleFieldChange('email', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('email', e.target.value)
                              }
                              placeholder="Enter email address"
                            />
                          </div>

                          <div className="form-field">
                            <label className="field-label">Phone Number</label>
                            <input
                              className="field-input"
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                )
                              }
                              value={getFieldValue('phone')}
                              type="tel"
                              onChange={e =>
                                handleFieldChange('phone', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('phone', e.target.value)
                              }
                              placeholder="Enter phone number"
                            />
                          </div>

                          {singleContactState.contact?.contactType ===
                            'individual' && (
                            <>
                              <div className="form-field">
                                <label className="field-label">Job Title</label>
                                <input
                                  className="field-input"
                                  disabled={
                                    !isAuthorized(
                                      layoutState.userRoles,
                                      editCompanyContactsRolesList,
                                    )
                                  }
                                  value={
                                    getFieldValue('jobTitle') ||
                                    singleContactState.contact?.jobTitle ||
                                    ''
                                  }
                                  type="text"
                                  onChange={e =>
                                    handleFieldChange(
                                      'jobTitle',
                                      e.target.value,
                                    )
                                  }
                                  onBlur={e =>
                                    handleFieldBlur('jobTitle', e.target.value)
                                  }
                                  placeholder="Enter job title"
                                />
                              </div>

                              <div className="form-field">
                                <label className="field-label">
                                  Company Name
                                </label>
                                <input
                                  className="field-input"
                                  disabled={
                                    !isAuthorized(
                                      layoutState.userRoles,
                                      editCompanyContactsRolesList,
                                    )
                                  }
                                  value={
                                    getFieldValue('companyName') ||
                                    singleContactState.contact?.companyName ||
                                    ''
                                  }
                                  type="text"
                                  onChange={e =>
                                    handleFieldChange(
                                      'companyName',
                                      e.target.value,
                                    )
                                  }
                                  onBlur={e =>
                                    handleFieldBlur(
                                      'companyName',
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Enter company name"
                                />
                              </div>
                            </>
                          )}

                          {singleContactState.contact?.contactType ===
                            'company' && (
                            <>
                              <div className="form-field">
                                <label className="field-label">
                                  Corporate Formation
                                </label>
                                <input
                                  className="field-input"
                                  disabled={
                                    !isAuthorized(
                                      layoutState.userRoles,
                                      editCompanyContactsRolesList,
                                    )
                                  }
                                  value={
                                    getFieldValue(
                                      'companyCorporateFormation',
                                    ) ||
                                    singleContactState.contact
                                      ?.companyCorporateFormation ||
                                    ''
                                  }
                                  type="text"
                                  onChange={e =>
                                    handleFieldChange(
                                      'companyCorporateFormation',
                                      e.target.value,
                                    )
                                  }
                                  onBlur={e =>
                                    handleFieldBlur(
                                      'companyCorporateFormation',
                                      e.target.value,
                                    )
                                  }
                                  placeholder="e.g., LLC, Corporation, Partnership"
                                />
                              </div>

                              <div className="form-field">
                                <label className="field-label">
                                  Formation State
                                </label>
                                <input
                                  className="field-input"
                                  disabled={
                                    !isAuthorized(
                                      layoutState.userRoles,
                                      editCompanyContactsRolesList,
                                    )
                                  }
                                  value={
                                    getFieldValue(
                                      'companyCorporateFormationState',
                                    ) ||
                                    singleContactState.contact
                                      ?.companyCorporateFormationState ||
                                    ''
                                  }
                                  type="text"
                                  onChange={e =>
                                    handleFieldChange(
                                      'companyCorporateFormationState',
                                      e.target.value,
                                    )
                                  }
                                  onBlur={e =>
                                    handleFieldBlur(
                                      'companyCorporateFormationState',
                                      e.target.value,
                                    )
                                  }
                                  placeholder="State of incorporation"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Address Information */}
                      <div className="form-section">
                        <h3 className="section-title">Address Information</h3>
                        <div className="form-grid">
                          <div className="form-field full-width">
                            <label className="field-label">
                              Street Address
                            </label>
                            <input
                              className="field-input"
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                )
                              }
                              value={getFieldValue('street')}
                              type="text"
                              onChange={e =>
                                handleFieldChange('street', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('street', e.target.value)
                              }
                              placeholder="Enter street address"
                            />
                          </div>

                          <div>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '6px',
                              }}
                            >
                              City
                            </label>
                            <input
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                              }}
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                )
                              }
                              value={getFieldValue('city')}
                              type="text"
                              onChange={e =>
                                handleFieldChange('city', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('city', e.target.value)
                              }
                              placeholder="Enter city"
                            />
                          </div>

                          <div>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '6px',
                              }}
                            >
                              State/Province
                            </label>
                            <input
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                              }}
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                )
                              }
                              value={getFieldValue('state')}
                              type="text"
                              onChange={e =>
                                handleFieldChange('state', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('state', e.target.value)
                              }
                              placeholder="Enter state or province"
                            />
                          </div>

                          <div>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '6px',
                              }}
                            >
                              ZIP/Postal Code
                            </label>
                            <input
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                              }}
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                )
                              }
                              value={getFieldValue('zipCode')}
                              type="text"
                              onChange={e =>
                                handleFieldChange('zipCode', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('zipCode', e.target.value)
                              }
                              placeholder="Enter ZIP or postal code"
                            />
                          </div>

                          <div>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '6px',
                              }}
                            >
                              Country
                            </label>
                            <input
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                              }}
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                )
                              }
                              value={getFieldValue('country')}
                              type="text"
                              onChange={e =>
                                handleFieldChange('country', e.target.value)
                              }
                              onBlur={e =>
                                handleFieldBlur('country', e.target.value)
                              }
                              placeholder="Enter country"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Company Signers */}
                      {(contactType === 'company' ||
                        props.contactType === 'company') && (
                        <div className="form-section">
                          <h3 className="section-title">Authorized Signers</h3>
                          <div className="signers-list">
                            {companyPOCList?.map((signer, i) => (
                              <div key={signer._id} className="signer-card">
                                <CompanyPoc
                                  id={signer._id}
                                  name={signer.name}
                                  email={signer.email}
                                  title={signer.title}
                                  defaultSigner={signer.defaultSigner}
                                  onValueChange={(field, value) =>
                                    onPOCValueChange(i, field, value)
                                  }
                                  onRemovePOC={onRemovePOC}
                                />
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={onAddNewPOC}
                              disabled={
                                !isAuthorized(
                                  layoutState.userRoles,
                                  editCompanyContactsRolesList,
                                )
                              }
                              className="add-signer-btn"
                            >
                              <i className="gg-add" />
                              Add Signer
                            </button>
                          </div>
                        </div>
                      )}
                    </form>
                  </div>

                  {/* Sidebar with Notes */}
                  <div className="form-sidebar">
                    <div className="form-section w-100">
                      <h3 className="section-title">Notes</h3>
                      <textarea
                        className="field-textarea w-100"
                        disabled={
                          !isAuthorized(
                            layoutState.userRoles,
                            editCompanyContactsRolesList,
                          )
                        }
                        value={
                          getFieldValue('notes') ||
                          singleContactState.contact?.notes ||
                          ''
                        }
                        onChange={e =>
                          handleFieldChange('notes', e.target.value)
                        }
                        onBlur={e => handleFieldBlur('notes', e.target.value)}
                        placeholder="Add any additional notes about this contact..."
                        rows={6}
                      />
                    </div>
                  </div>
                </div>
              </TabPanel>

              {/* Tab 2: Documents */}
              <TabPanel>
                <div className="tab-panel-content">
                  {singleContactState.isFetchingContact ? (
                    <div className="loading-state">
                      <div className="loading-icon">
                        <i className="gg-spinner" />
                      </div>
                      <p>Loading documents...</p>
                    </div>
                  ) : singleContactState.contactAgreements?.length > 0 ? (
                    <DataTable
                      pagination
                      columns={[
                        {
                          name: 'Document Title',
                          sortable: true,
                          grow: 2,
                          cell: row => (
                            <div className="document-title-cell">
                              <i className="gg-file-document document-icon" />
                              <Link
                                to={routes.viewCompanyDocument(
                                  layoutState.selectedCompany?._id,
                                  row.agrUID,
                                )}
                                className="document-link"
                              >
                                {row.agreement.agreementTitle}
                              </Link>
                            </div>
                          ),
                        },
                        {
                          name: 'Status',
                          sortable: true,
                          center: true,
                          cell: row => {
                            const status = getDocumentStatus(
                              row.agreement?.agreementStatus,
                            );
                            return (
                              <span
                                className={`status-badge status-${status.text
                                  .toLowerCase()
                                  .replace(' ', '-')}`}
                                style={{
                                  color: status.color,
                                  backgroundColor: status.bg,
                                  border: `1px solid ${status.color}20`,
                                }}
                              >
                                {status.text}
                              </span>
                            );
                          },
                        },
                        {
                          name: 'Date Created',
                          sortable: true,
                          center: true,
                          cell: row => (
                            <span className="date-cell">
                              {new Date(
                                row.dates?.draftDate,
                              ).toLocaleDateString()}
                            </span>
                          ),
                        },
                        {
                          name: 'Actions',
                          center: true,
                          width: '100px',
                          cell: row => (
                            <button
                              onClick={() => {
                                setQuickViewDocument(row);
                                setIsQuickViewOpen(true);
                              }}
                              className="action-btn"
                            >
                              <i className="gg-eye" />
                            </button>
                          ),
                        },
                      ]}
                      data={singleContactState.contactAgreements}
                      customStyles={{
                        headCells: {
                          style: {
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            backgroundColor: '#f9fafb',
                          },
                        },
                        cells: {
                          style: {
                            fontSize: '14px',
                          },
                        },
                      }}
                    />
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <i className="gg-file-document" />
                      </div>
                      <h4 className="empty-title">No documents found</h4>
                      <p className="empty-description">
                        No documents are currently associated with this contact.
                      </p>
                    </div>
                  )}
                </div>
              </TabPanel>

              {/* Tab 3: History */}
              <TabPanel>
                <div className="tab-panel-content">
                  {singleContactState.contactAgreements?.length > 0 ? (
                    <div className="history-list">
                      {singleContactState.contactAgreements
                        .flatMap(agreement =>
                          (agreement.history || []).map(historyItem => ({
                            ...historyItem,
                            documentTitle: agreement.agreement?.agreementTitle,
                            documentId: agreement.agrUID,
                          })),
                        )
                        .sort(
                          (a, b) =>
                            new Date(b.date).getTime() -
                            new Date(a.date).getTime(),
                        )
                        .map((historyItem, index) => {
                          const getActionDescription = (action, data) => {
                            switch (action) {
                              case 'signed':
                                return 'signed the document';
                              case 'approved':
                                return 'approved the document';
                              case 'declined':
                                return 'declined the document';
                              case 'viewed':
                                return 'viewed the document';
                              case 'submitted':
                                return `sent document to ${
                                  data?.partyName || 'recipient'
                                }`;
                              case 'created':
                                return 'created the document';
                              case 'updated':
                                return 'updated the document';
                              default:
                                return action || 'performed an action';
                            }
                          };

                          return (
                            <div
                              key={`${historyItem.documentId}-${index}`}
                              className="history-item"
                            >
                              <div className="history-item-header">
                                <div className="history-item-content">
                                  <div className="history-document-title">
                                    <Link
                                      to={routes.viewCompanyDocument(
                                        layoutState.selectedCompany?._id,
                                        historyItem.documentId,
                                      )}
                                      className="history-document-link"
                                    >
                                      {historyItem.documentTitle}
                                    </Link>
                                  </div>
                                  <div className="history-action">
                                    <strong>
                                      {historyItem.party ||
                                        historyItem.data?.email ||
                                        'System'}
                                    </strong>{' '}
                                    {getActionDescription(
                                      historyItem.action,
                                      historyItem.data,
                                    )}
                                  </div>
                                </div>
                                <div className="history-date">
                                  {new Date(
                                    historyItem.date,
                                  ).toLocaleDateString()}{' '}
                                  at{' '}
                                  {new Date(
                                    historyItem.date,
                                  ).toLocaleTimeString()}
                                </div>
                              </div>
                              {historyItem.data?.comment && (
                                <div className="history-comment">
                                  "{historyItem.data.comment}"
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <i className="gg-time" />
                      </div>
                      <h4 className="empty-title">No history found</h4>
                      <p className="empty-description">
                        No document history is available for this contact.
                      </p>
                    </div>
                  )}
                </div>
              </TabPanel>
            </Tabs>
          </div>
        ) : (
          <div className="page-loading-state">
            <div className="page-loading-icon">
              <i className="gg-spinner" />
            </div>
            <h2 className="page-loading-title">Loading Contact</h2>
            <p className="page-loading-description">
              Please wait while we load the contact details...
            </p>
          </div>
        )}

        {/* Quick View Modal */}
        {isQuickViewOpen && quickViewDocument && (
          <QuickViewModal
            isOpen={isQuickViewOpen}
            onClose={() => {
              setIsQuickViewOpen(false);
              setQuickViewDocument(null);
            }}
            document={quickViewDocument}
            companyId={layoutState.selectedCompany?._id || ''}
          />
        )}
      </LayoutWrapper>
    </>
  );
}
